import { useEffect, useState } from "react";
import { HandHeart, PiggyBank, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { Celebration, FormDialog } from "@/components/kids/kidsUi";
import PiggyStage from "@/components/piggy/PiggyStage";
import GlassJar from "@/components/piggy/GlassJar";

const KBTN = "inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl px-5 font-display text-base font-extrabold";
const DIALOG = {
  save: { title: "Move to Save", hint: "Moves money from Spend to Save right away.", reason: null },
  save_use: { title: "Use Save money", hint: "A grown-up will say yes or not now.", reason: "What is it for?" },
  give: { title: "I want to give", hint: "A grown-up will say yes or not now.", reason: "Cause, e.g. Animal shelter" },
};

function KidMoneyDialog({ mode, hubId, kidId, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const d = DIALOG[mode];
  const go = async () => {
    setErr("");
    try {
      if (mode === "save") await api.post(`/organizations/${hubId}/piggy-bank/${kidId}/jars/move-to-save`, { amount: Number(amount) });
      else await api.post(`/organizations/${hubId}/piggy-bank/${kidId}/requests`, { kind: mode, amount: Number(amount), reason });
      toast.success(mode === "save" ? `Saved ${money(amount)}!` : "Sent to a grown-up");
      onDone();
    } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <FormDialog open onClose={onClose} title={d.title} description={d.hint} testid={`jar-dialog-${mode}`}>
      <div className="space-y-3">
        <input type="number" min="0.01" step="0.01" inputMode="decimal" className="q-input min-h-[56px] text-lg" placeholder="Amount ($)" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="jar-amount-input" />
        {d.reason && <input className="q-input min-h-[56px] text-lg" maxLength={80} placeholder={d.reason} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="jar-reason-input" />}
        {err && <p className="q-error" data-testid="jar-error">{err}</p>}
        <button className={`${KBTN} w-full bg-[rgb(var(--glow-kids))] text-white`} onClick={go} data-testid="jar-submit-btn">{mode === "save" ? "Move to Save" : mode === "give" ? "I want to give" : "Ask"}</button>
      </div>
    </FormDialog>
  );
}

// With Jars on: the glass piggy is Spend; Save and Give sit beside it.
export default function JarsPanel({ hubId, bank, isKid, onChanged, onBank }) {
  const [dialog, setDialog] = useState(null);
  const [heart, setHeart] = useState(null);
  const j = bank.jars;
  const kidId = bank.kid.id;
  useEffect(() => {
    if (isKid && j.hearts.length) { setHeart(j.hearts[0]); api.post(`/organizations/${hubId}/piggy-bank/${kidId}/hearts-seen`).catch(() => {}); }
  }, [isKid, j.hearts.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const declined = j.requests.filter((r) => r.status === "declined").slice(0, 2);
  const pending = j.requests.filter((r) => r.status === "pending");
  return (
    <div className="space-y-5" data-testid="jars-panel">
      <div className="grid items-end gap-4 sm:grid-cols-[1fr_auto_auto]">
        <div data-testid="jar-spend">
          <p className="text-center font-display text-sm font-extrabold uppercase tracking-[0.14em]" style={{ color: "rgb(var(--glow-kids))" }}>Spend</p>
          {isKid ? <PiggyStage hubId={hubId} kidId={kidId} size="lg" poll controls onBank={onBank} /> : <p className="text-center font-display text-3xl font-extrabold text-q-text">{money(j.spend)}</p>}
          <span className="sr-only" data-testid="jar-spend-balance">{money(j.spend)}</span>
        </div>
        <div className="flex justify-center gap-4 sm:contents">
          <GlassJar kidId={kidId} jar="save" label="Save" balance={j.save} testid="jar-save" />
          <GlassJar kidId={kidId} jar="give" label="Give" balance={j.give} testid="jar-give" />
        </div>
      </div>
      <p className="text-center font-display text-xl font-extrabold text-q-text" data-testid="jars-total">Total {money(j.total)}</p>
      {isKid && (
        <div className="grid gap-3 sm:grid-cols-3">
          <button className={`${KBTN} bg-[rgb(var(--glow-money))] text-white`} onClick={() => setDialog("save")} data-testid="move-to-save-btn"><PiggyBank className="h-5 w-5" /> Move to Save</button>
          <button className={`${KBTN} border border-q-line bg-q-raised text-q-text`} onClick={() => setDialog("save_use")} data-testid="use-save-btn"><Wallet className="h-5 w-5" /> Use Save money</button>
          <button className={`${KBTN} bg-[rgb(var(--glow-kids))] text-white`} onClick={() => setDialog("give")} data-testid="give-btn"><HandHeart className="h-5 w-5" /> I want to give</button>
        </div>
      )}
      {pending.map((r) => <p key={r.id} className="rounded-2xl bg-q-raised p-3 text-sm font-semibold text-q-text" data-testid={`jar-request-${r.id}`}>Waiting for a grown-up: {r.kind === "give" ? `give ${money(r.amount)} to ${r.reason}` : `use ${money(r.amount)} of Save ${r.reason}`}</p>)}
      {declined.map((r) => <p key={r.id} className="rounded-2xl bg-q-raised p-3 text-sm font-semibold text-q-muted" data-testid={`jar-declined-${r.id}`}>Not right now: {r.note}</p>)}
      {dialog && <KidMoneyDialog mode={dialog} hubId={hubId} kidId={kidId} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged(); }} />}
      {heart && <Celebration emoji="💖" big text={`You gave ${money(heart.amount)} to ${heart.cause}!`} onDone={() => setHeart(null)} testid="heart-celebration" />}
    </div>
  );
}
